import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getAllProperties, checkSession } from "../utils/api";

// ✅ Shared components
import AdminNavbar from "./AdminNavbar";
import Banner from "./ui/Banner";

// ✅ Properties subcomponents
import PropertyTable from "./properties/PropertyTable";
import PropertyFormModal from "./properties/PropertyFormModal";
import PropertyImagesModal from "./properties/PropertyImagesModal";
import DeleteConfirmModal from "./properties/DeleteConfirmModal";

function PropertiesMaintenance() {
  const [properties, setProperties] = useState([]);
  const [session, setSession] = useState(null);

  // ✅ Banner state
  const [banner, setBanner] = useState({ type: "", message: "" });

  // ✅ Modal states
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [showImagesModal, setShowImagesModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // ✅ Form + IDs
  const [form, setForm] = useState({
    title: "",
    description: "",
    price: "",
    location: "",
    featured: false,
  });
  const [editingId, setEditingId] = useState(null);
  const [imagesPropertyId, setImagesPropertyId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const navigate = useNavigate();

  // ✅ Refresh helper
  const refresh = () =>
    getAllProperties()
      .then((res) => {
        if (res?.success) {
          setProperties(res.properties || []);
        } else {
          setBanner({
            type: "danger",
            message: res?.message || "Failed to load properties.",
          });
        }
      })
      .catch((err) => {
        console.error("Error fetching properties:", err);
        setBanner({ type: "danger", message: "Error fetching properties." });
      });

  // ✅ Session check
  useEffect(() => {
    checkSession()
      .then((res) => {
        if (!res.loggedIn) {
          navigate("/login");
        } else {
          setSession(res);
          refresh();
        }
      })
      .catch(() => navigate("/login"));
  }, [navigate]);

  return (
    <div>
      <AdminNavbar onLogout={() => navigate("/")} />

      <div className="container mt-5 pt-4">
        <h2>🏠 Properties Maintenance</h2>

        {/* ✅ Banner feedback */}
        <Banner
          type={banner.type}
          message={banner.message}
          onClose={() => setBanner({ type: "", message: "" })}
        />

        {/* ✅ Add property button */}
        <button
          className="btn btn-primary mb-3"
          onClick={() => setShowCreateModal(true)}
        >
          Add Property
        </button>

        {/* ✅ Property table */}
        <PropertyTable
          properties={properties}
          session={session}
          onEdit={(p) => {
            setEditingId(p.id);
            setForm({
              title: p.title || "",
              description: p.description || "",
              price: p.price || "",
              location: p.location || "",
              featured: Number(p.featured) === 1,
            });
            setShowUpdateModal(true);
          }}
          onImages={(id) => {
            setImagesPropertyId(id);
            setShowImagesModal(true);
          }}
          onDelete={(id) => {
            setDeleteId(id);
            setShowDeleteModal(true);
          }}
        />

        {/* ✅ Modals */}
        <PropertyFormModal
          show={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          mode="create"
          form={form}
          setForm={setForm}
          refresh={refresh}
          setBanner={setBanner}
        />

        <PropertyFormModal
          show={showUpdateModal}
          onClose={() => setShowUpdateModal(false)}
          mode="update"
          form={form}
          setForm={setForm}
          editingId={editingId}
          refresh={refresh}
          setBanner={setBanner}
        />

        <PropertyImagesModal
          show={showImagesModal}
          onClose={() => setShowImagesModal(false)}
          propertyId={imagesPropertyId}
          refresh={refresh}
          setBanner={setBanner}
        />

        <DeleteConfirmModal
          show={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          propertyId={deleteId}
          refresh={refresh}
          setBanner={setBanner}
        />
      </div>
    </div>
  );
}

export default PropertiesMaintenance;
